import {Snackbar, Typography, Tabs, Tab, Box, Grid} from '@mui/material'
import React, {useEffect, useState} from 'react'
import moment from 'moment-timezone'
import SnackbarAlertCustom from 'src/components/elements/SnackbarAlertCustom/SnackbarAlertCustom'
import apiClient from 'src/services/apiClient'
import {STATUS} from 'src/constants/constants'
import Chart from 'src/components/elements/Chart/Chart'
import Filters, {FilterItem} from 'src/components/templates/Filters/Filters'
import CardCustom from 'src/components/elements/CardCustom/CardCustom'

const Graphs: React.FC<{patientId: string}> = ({patientId}) => {
  const [status, setStatus] = useState<any>(undefined)
  const [graphData, setGraphData] = useState<any>({})
  const [tabValues, setTabValues] = useState<Record<string, string>>({})
  const [filters, setFilters] = useState<Record<string, string | string[]>[]>([])

  const filterConfig: FilterItem[] = [
    {
      id: 'date',
      label: 'Data',
      type: 'rangePicker'
    }
  ]

  const onSubmit = (values: any) => {
    setFilters(values)
  }

  useEffect(() => {
    fetchGraphsData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  const fetchGraphsData = async () => {
    const fetchedData = await apiClient.getPatientQuestionnaireGraphs(patientId, filters, setStatus) ?? []
    const processedData = processGraphData(fetchedData)
    setGraphData(processedData)

    if (Object.keys(processedData).length > 0) {
      const initialTabValues: Record<string, string> = {}
      for (const key in processedData) {
        if (processedData[key] && processedData[key].length > 0) {
          initialTabValues[key] = processedData[key][0].key
        }
      }
      setTabValues(initialTabValues)
    }
  }

  const processGraphData = (data: any[]) => {
    if (!Array.isArray(data)) return {}

    const processed: Record<string, any[]> = {}

    data.forEach((questionnaire, index) => {
      const {title, formSchemaId, questionnaireWithScores} = questionnaire
      if (!questionnaireWithScores || questionnaireWithScores.length === 0) return

      const groupKey = `${title}-${formSchemaId}-${index}`
      processed[groupKey] = []

      const scoreKeys: Record<string, string> = {}
      questionnaireWithScores.forEach((submission: any) => {
        Object.keys(submission.scores).forEach(key => {
          if (!scoreKeys[key]) {
            scoreKeys[key] = submission.scores[key].label
          }
        })
      })

      const xAxis = questionnaireWithScores.map((s: any) => moment(s.date).tz('Europe/Rome').format('DD/MM/YYYY'))

      Object.keys(scoreKeys).forEach(scoreKey => {
        const seriesData = questionnaireWithScores.map((s: any) => {
          const score = s.scores[scoreKey]
          return score && score.value ? parseFloat(score.value) : null
        })

        processed[groupKey].push({
          key: scoreKey,
          label: scoreKeys[scoreKey],
          title: title,
          xAxis: xAxis,
          series: [{
            name: scoreKeys[scoreKey],
            data: seriesData,
            type: 'line'
          }]
        })
      })
    })

    return processed
  }

  const handleTabChange = (graphKey: string, newValue: string) => {
    setTabValues(prev => ({...prev, [graphKey]: newValue}))
  }

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '10px'}}>
        <Filters config={filterConfig} key='filters' onSubmit={onSubmit} />
      </div>
      <Grid container spacing={2}>
        {Object.entries(graphData).map(([graphKey, graphItems]) => {
          if (!Array.isArray(graphItems) || graphItems.length === 0) return null

          const selectedTabKey = tabValues[graphKey]
          const selectedGraph = graphItems.find(graph => graph.key === selectedTabKey)
          const title = graphItems[0]?.title

          return (
            <Grid item key={graphKey} md={12}>
              <CardCustom title={<Typography fontWeight={700} variant='body2'>{title}</Typography>}>
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                  <Tabs
                    onChange={(_, newValue) => handleTabChange(graphKey, newValue)}
                    scrollButtons='auto'
                    value={selectedTabKey || false}
                    variant='scrollable'
                  >
                    {graphItems.map(graph => (
                      <Tab key={graph.key} label={graph.label} value={graph.key} />
                    ))}
                  </Tabs>
                </Box>

                {selectedGraph ? (
                  <Box sx={{padding: '20px 0'}}>
                    <Chart
                      colors={['#4472C4']}
                      data={selectedGraph}
                      legend={false}
                      type='line'
                    />
                  </Box>
                ) : (
                  <Box sx={{backgroundColor: '#FCF8E3', padding: '15px', color: '#8a6d3b'}}>
                    <Typography>{'Non ci sono statistiche per questo grafico'}</Typography>
                  </Box>
                )}
              </CardCustom>
            </Grid>
          )
        })}
      </Grid>
      <Snackbar
        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
        autoHideDuration={2000}
        onClose={() => { setStatus(undefined) }}
        open={status === STATUS.REJECTED}
      >
        <div>
          <SnackbarAlertCustom
            onClose={() => { setStatus(undefined) }}
            severity='error'
            sx={{width: '100%'}}
          >
            <Typography variant='h5'>
              {'Errore nel caricare i questionari'}
            </Typography>
          </SnackbarAlertCustom>
        </div>
      </Snackbar>
    </>
  )
}

export default Graphs
